import { Block } from '../classes/Block';
import { VideoSettings } from '../components/VideoSettings';
import { getApiUrl } from './api';
import { authFetch } from './authFetch';
import { CardGroup } from './cardGroups';
import { ApiBlock, ApiCardGroup } from './apiTypes';
import { sanitizeFileNameSegment } from './stringSanitization';

export type ExportFormat = 'png' | 'tiff' | 'pdf';

export interface ExportConfig {
  format: ExportFormat;
  mimeType: string;
  fileExtension: string;
  overrideColors?: boolean; // For PDF: force black text and white background
}

export const EXPORT_CONFIGS: Record<ExportFormat, ExportConfig> = {
  png: {
    format: 'png',
    mimeType: 'image/png',
    fileExtension: 'png',
  },
  tiff: {
    format: 'tiff',
    mimeType: 'image/tiff',
    fileExtension: 'tiff',
  },
  pdf: {
    format: 'pdf',
    mimeType: 'application/pdf',
    fileExtension: 'pdf',
    overrideColors: true,
  },
};

export function transformBlockToApiFormat(
  block: Block,
  columnDefs: any[],
  overrideColors: boolean = false
): ApiBlock {
  const rows: string[][] = [];
  if (block.dataRows && block.dataRows.length > 0) {
    for (const dataRow of block.dataRows) {
      const rowValues: string[] = [];
      for (const colDef of columnDefs) {
        let value: string = String(dataRow[colDef.field] || '');

        // Special handling for logo blocks: ensure col2 is JSON encoded with scale key
        if (value && block.isLogoBlock && colDef.field === 'col2') {
          try {
            // If value is already a JSON string, do nothing
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _ = JSON.parse(value);
          } catch {
            const scaleValue: number = parseFloat(value);
            value = JSON.stringify({ scale: isNaN(scaleValue) ? 100 : scaleValue });
          }
        }
        rowValues.push(value);
      }
      rows.push([...rowValues]);
    }
  }

  return {
    name: block.name,
    properties: block.toRenderApiProperties(overrideColors),
    rows: rows,
  };
}

export interface ExportOptions {
  projectId: string;
  projectName: string;
  blocks: Block[];
  cardGroups?: CardGroup[];
  columnDefs: any[];
  videoSettings: VideoSettings;
  config: ExportConfig;
  transparent?: boolean;
}

function buildCardsExportData(
  blocks: Block[],
  cardGroups: CardGroup[],
  columnDefs: any[],
  overrideColors: boolean
): ApiCardGroup[] {
  const blockById: Map<string, Block> = new Map<string, Block>(blocks.map((block: Block) => [block.id, block]));
  const assignedIds: Set<string> = new Set<string>(cardGroups.flatMap((group: CardGroup) => group.blockIds));
  const unassignedBlocks: Block[] = blocks.filter((block: Block) => !assignedIds.has(block.id));

  const groupedCards: ApiCardGroup[] = cardGroups
    .map((group: CardGroup) => ({
      card: group.blockIds
        .map((blockId: string) => blockById.get(blockId))
        .filter((block: Block | undefined): block is Block => block !== undefined)
        .map((block: Block) => transformBlockToApiFormat(block, columnDefs, overrideColors)),
    }))
    .filter((group: ApiCardGroup) => group.card.length > 0);

  if (unassignedBlocks.length > 0) {
    groupedCards.push({
      card: unassignedBlocks.map((block: Block) => transformBlockToApiFormat(block, columnDefs, overrideColors)),
    });
  }

  return groupedCards;
}

export async function exportToFormat(options: ExportOptions): Promise<void> {
  const {
    projectId,
    projectName,
    blocks,
    cardGroups = [],
    columnDefs,
    videoSettings,
    config,
    transparent = false,
  } = options;
  const shouldGroupByCards: boolean = videoSettings.mode === 'cards';
  const overrideColors: boolean = config.overrideColors ?? false;
  const exportData: ApiBlock[] | ApiCardGroup[] = shouldGroupByCards
    ? buildCardsExportData(blocks, cardGroups, columnDefs, overrideColors)
    : blocks.map((block: Block) => transformBlockToApiFormat(block, columnDefs, overrideColors));

  const payload = {
    video_settings: {
      background_color: overrideColors ? '#FFFFFF' : videoSettings.background_color,
      fps_num: videoSettings.fps_num,
      resolution: videoSettings.resolution,
      transparent: videoSettings.transparent,
      video_time: videoSettings.video_time,
      aspect_ratio: videoSettings.aspect_ratio,
      fonts: JSON.stringify(videoSettings.fonts),
      logos: JSON.stringify(videoSettings.logos),
      mode: videoSettings.mode,
      seconds_per_card: videoSettings.seconds_per_card,
    },
    data: exportData,
    transparent,
  };

  const response = await authFetch(`${getApiUrl()}/export-png?projectId=${projectId}&format=${config.format}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Failed to export ${config.format.toUpperCase()}`);
  }

  const arrayBuffer = await response.arrayBuffer();
  const blob = new Blob([arrayBuffer], { type: config.mimeType });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  const now: Date = new Date();
  const padding = (n: number): string => String(n).padStart(2, '0');
  const ts: string = `${now.getFullYear()}-${padding(now.getMonth() + 1)}-${padding(now.getDate())}_${padding(now.getHours())}-${padding(now.getMinutes())}`;
  const safeName: string = sanitizeFileNameSegment(projectName, { fallback: 'export', lowercase: false });
  link.download = `Fintitles_${safeName}_${ts}.${config.fileExtension}`;
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

